import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Menu,
  MenuGroupHeader,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Overflow,
  OverflowItem,
  makeStyles,
  mergeClasses,
  tokens,
  useIsOverflowItemVisible,
  useOverflowMenu,
} from '@fluentui/react-components';
import { ChevronDownRegular, MoreHorizontalRegular } from '@fluentui/react-icons';
import {
  portalColors,
  portalSpacing,
  portalTypography,
} from '../../styles/theme';
import { useActivityMetricsAccess } from '../../hooks/usePortalFeatures';

interface NavLinkItem {
  label: string;
  path: string;
}

const ACTIVITY_PATH = '/activity';
const CREDENTIALS_PATH = '/credentials';

const NAV_LINKS: readonly NavLinkItem[] = [
  { label: 'Home', path: '/' },
  { label: 'Getting Started', path: '/getting-started' },
  { label: 'APIs', path: '/apis' },
  { label: 'Credentials', path: CREDENTIALS_PATH },
  { label: 'Activity', path: ACTIVITY_PATH },
];

/**
 * Companion Commerce Portal cross-link (build-time per environment). Commerce
 * self-service lives in its own SPA; the nav item renders only when the URL is
 * configured — same-tab navigation because the two portals are companion apps
 * sharing the CIAM identity.
 */
const COMMERCE_PORTAL_URL = import.meta.env.VITE_COMMERCE_PORTAL_URL;
const COMMERCE_PORTAL_OVERFLOW_ID = 'nav-commerce-portal';

const OPS_LINKS: readonly NavLinkItem[] = [
  { label: 'Dashboard', path: '/ops/dashboard' },
  { label: 'Credential Reviews', path: '/ops/credential-reviews' },
  { label: 'Internal Clients', path: '/ops/internal-clients' },
];

// Stable overflow id for the Ops dropdown so it can be matched in the
// overflow menu. The Ops group overflows before any primary link.
const OPS_OVERFLOW_ID = 'nav-ops';

// Higher priority overflows later (stays visible longer). Primary links keep
// their authoring order (Home stays longest); Ops drops first at priority 0.
function navLinkPriority(index: number, linkCount: number): number {
  return linkCount - index;
}

const useStyles = makeStyles({
  // The Overflow container. `minWidth: 0` lets it shrink inside the nav flex
  // row below its content width, which is what lets the overflow manager detect
  // that items no longer fit. `overflowX: hidden` clips while the manager
  // toggles `display: none` on the items it decides are overflowing.
  container: {
    display: 'flex',
    flexWrap: 'nowrap',
    alignItems: 'center',
    flexGrow: 1,
    minWidth: 0,
    overflowX: 'hidden',
    overflowY: 'hidden',
    gap: '0',
  },
  // Items must not shrink — the manager hides whole items rather than squishing
  // their text (which is what produced the one-word-per-line breakage).
  navLink: {
    flexShrink: 0,
    textDecorationLine: 'none',
    color: portalColors.bodyText,
    fontSize: portalTypography.navLink,
    fontWeight: portalTypography.weightRegular,
    fontFamily: portalTypography.fontFamilyBase,
    paddingLeft: portalSpacing.xl,
    paddingRight: portalSpacing.xl,
    lineHeight: portalTypography.navLinkLineHeight,
    ':hover': {
      color: portalColors.brand,
      textDecorationLine: 'none',
    },
  },
  navLinkActive: {
    fontWeight: portalTypography.weightSemibold,
  },
  opsItem: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },
  opsButton: {
    fontSize: portalTypography.navLink,
    fontWeight: portalTypography.weightRegular,
    fontFamily: portalTypography.fontFamilyBase,
    color: portalColors.bodyText,
    backgroundColor: 'transparent',
    borderTopWidth: '0',
    borderRightWidth: '0',
    borderBottomWidth: '0',
    borderLeftWidth: '0',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: portalSpacing.xxs,
    paddingLeft: portalSpacing.xl,
    paddingRight: portalSpacing.xl,
    lineHeight: portalTypography.navLinkLineHeight,
    ':hover': {
      color: portalColors.brand,
    },
  },
  opsButtonActive: {
    fontWeight: portalTypography.weightSemibold,
  },
  // The "more" (overflow) trigger. Fluent pins it via `[data-overflow-menu]`,
  // so it stays visible and reserves its own width while items collapse into it.
  overflowTrigger: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: portalColors.bodyText,
    backgroundColor: 'transparent',
    borderTopWidth: '0',
    borderRightWidth: '0',
    borderBottomWidth: '0',
    borderLeftWidth: '0',
    cursor: 'pointer',
    paddingLeft: portalSpacing.m,
    paddingRight: portalSpacing.m,
    fontSize: portalTypography.docs,
    lineHeight: portalTypography.navLinkLineHeight,
    ':hover': {
      color: portalColors.brand,
    },
  },
  menuLink: {
    textDecorationLine: 'none',
    color: tokens.colorNeutralForeground1,
  },
});

type NavStyles = ReturnType<typeof useStyles>;

function isPathActive(currentPath: string, linkPath: string): boolean {
  if (linkPath === '/') {
    return currentPath === '/';
  }
  return currentPath.startsWith(linkPath);
}

/**
 * A primary link inside the overflow menu. Renders only while its matching
 * in-bar item is hidden, so the menu and the bar never show the same link twice.
 */
function NavOverflowMenuItem({
  link,
  styles,
}: {
  link: NavLinkItem;
  styles: NavStyles;
}): JSX.Element | null {
  const isVisible = useIsOverflowItemVisible(link.path);

  if (isVisible) {
    return null;
  }

  return (
    <Link to={link.path} className={styles.menuLink}>
      <MenuItem>{link.label}</MenuItem>
    </Link>
  );
}

/**
 * The Commerce Portal cross-link inside the overflow menu — a plain anchor
 * (external origin), rendered only while the in-bar item is hidden.
 */
function CommercePortalOverflowMenuItem({
  url,
  styles,
}: {
  url: string;
  styles: NavStyles;
}): JSX.Element | null {
  const isVisible = useIsOverflowItemVisible(COMMERCE_PORTAL_OVERFLOW_ID);

  if (isVisible) {
    return null;
  }

  return (
    <a href={url} className={styles.menuLink}>
      <MenuItem>Commerce Portal</MenuItem>
    </a>
  );
}

/**
 * The Ops group inside the overflow menu. Renders the Ops sub-links under a
 * header only while the in-bar Ops dropdown is hidden.
 */
function OpsOverflowMenuItems({
  styles,
}: {
  styles: NavStyles;
}): JSX.Element | null {
  const isVisible = useIsOverflowItemVisible(OPS_OVERFLOW_ID);

  if (isVisible) {
    return null;
  }

  return (
    <>
      <MenuGroupHeader>Ops</MenuGroupHeader>
      {OPS_LINKS.map((link) => (
        <Link key={link.path} to={link.path} className={styles.menuLink}>
          <MenuItem>{link.label}</MenuItem>
        </Link>
      ))}
    </>
  );
}

/**
 * The overflow ("more") menu. `useOverflowMenu` measures real layout and
 * reports whether anything is currently overflowing; the menu is mounted only
 * when it is, so no chevron is shown while everything fits.
 */
function OverflowMenu({
  isOps,
  links,
  styles,
}: {
  isOps: boolean;
  links: readonly NavLinkItem[];
  styles: NavStyles;
}): JSX.Element | null {
  const { ref, isOverflowing, overflowCount } =
    useOverflowMenu<HTMLButtonElement>();

  if (!isOverflowing) {
    return null;
  }

  return (
    <Menu>
      <MenuTrigger disableButtonEnhancement>
        <button
          ref={ref}
          className={styles.overflowTrigger}
          aria-label={`${overflowCount} more navigation ${
            overflowCount === 1 ? 'item' : 'items'
          }`}
        >
          <MoreHorizontalRegular />
        </button>
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          {links.map((link) => (
            <NavOverflowMenuItem key={link.path} link={link} styles={styles} />
          ))}
          {COMMERCE_PORTAL_URL && (
            <CommercePortalOverflowMenuItem
              url={COMMERCE_PORTAL_URL}
              styles={styles}
            />
          )}
          {isOps && <OpsOverflowMenuItems styles={styles} />}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}

export interface NavMenuProps {
  currentPath: string;
  isAuthenticated: boolean;
  isOps: boolean;
}

/**
 * Desktop nav-menu (link list + Ops dropdown), collapsed by actual rendered
 * width rather than a fixed breakpoint. Fluent's `Overflow` measures which
 * items fit the available space between the logo and the auth actions and moves
 * the rest into the overflow ("more") menu.
 */
export function NavMenu({
  currentPath,
  isAuthenticated,
  isOps,
}: NavMenuProps): JSX.Element {
  const styles = useStyles();
  const { hasAccess: activityEnabled } = useActivityMetricsAccess();

  // Two links are gated. Credentials requires a signed-in session (the route
  // itself is a ProtectedRoute), so it is hidden while unauthenticated. Activity
  // is a server-enforced default-deny grant managed by Ops, so it is hidden
  // while unauthenticated, loading, or ungranted and appears only after the
  // grant check resolves. The backend enforces all regardless.
  const visibleLinks = useMemo(
    () =>
      NAV_LINKS.filter((link) => {
        if (link.path === CREDENTIALS_PATH) {
          return isAuthenticated;
        }
        if (link.path === ACTIVITY_PATH) {
          return activityEnabled;
        }
        return true;
      }),
    [activityEnabled, isAuthenticated],
  );

  return (
    <Overflow minimumVisible={0}>
      <div className={styles.container}>
        {visibleLinks.map((link, index) => (
          <OverflowItem
            key={link.path}
            id={link.path}
            priority={navLinkPriority(index, visibleLinks.length)}
          >
            <Link
              to={link.path}
              className={mergeClasses(
                styles.navLink,
                isPathActive(currentPath, link.path)
                  ? styles.navLinkActive
                  : undefined,
              )}
            >
              {link.label}
            </Link>
          </OverflowItem>
        ))}

        {COMMERCE_PORTAL_URL && (
          <OverflowItem id={COMMERCE_PORTAL_OVERFLOW_ID} priority={0}>
            <a href={COMMERCE_PORTAL_URL} className={styles.navLink}>
              Commerce Portal
            </a>
          </OverflowItem>
        )}

        {isOps && (
          <OverflowItem id={OPS_OVERFLOW_ID} priority={0}>
            <div className={styles.opsItem}>
              <Menu>
                <MenuTrigger disableButtonEnhancement>
                  <button
                    className={mergeClasses(
                      styles.opsButton,
                      currentPath.startsWith('/ops')
                        ? styles.opsButtonActive
                        : undefined,
                    )}
                  >
                    Ops <ChevronDownRegular />
                  </button>
                </MenuTrigger>
                <MenuPopover>
                  <MenuList>
                    {OPS_LINKS.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        className={styles.menuLink}
                      >
                        <MenuItem>{link.label}</MenuItem>
                      </Link>
                    ))}
                  </MenuList>
                </MenuPopover>
              </Menu>
            </div>
          </OverflowItem>
        )}

        <OverflowMenu isOps={isOps} links={visibleLinks} styles={styles} />
      </div>
    </Overflow>
  );
}
